import { useState } from 'react';
import { Warning, ArrowSquareOut, Copy, X, GoogleLogo } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * InAppBrowserBlockerModal — shown when a user inside Instagram / Messenger /
 * Facebook / TikTok WebView tries to sign in with Google. Google blocks OAuth
 * in these embedded browsers (Error 403: disallowed_useragent). We give the
 * user a clear path to open the page in their device's default browser.
 *
 * Props:
 *   open: boolean
 *   onClose: () => void
 *   appName: detected app name (e.g. "Instagram")
 *   os: 'ios' | 'android' | 'other'
 *   targetUrl: the URL the user should open in their real browser
 */
const InAppBrowserBlockerModal = ({ open, onClose, appName, os, targetUrl }) => {
  const [copied, setCopied] = useState(false);
  if (!open) return null;

  const url = targetUrl || (typeof window !== 'undefined' ? window.location.href : '');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied — paste it into Safari or Chrome');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy. Long-press the link to copy manually.');
    }
  };

  // Android: use an `intent://` URL to force Chrome to open. iOS has no
  // reliable equivalent — Safari URL scheme `x-safari-https://` is restricted.
  const openExternal = () => {
    if (os === 'android') {
      const stripped = url.replace(/^https?:\/\//, '');
      window.location.href = `intent://${stripped}#Intent;scheme=https;package=com.android.chrome;end`;
      return;
    }
    // iOS / unknown — best-effort. Most in-app browsers expose a "Open in Browser"
    // option in their share menu; we surface instructions instead.
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      data-testid="inapp-blocker-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inapp-blocker-title"
    >
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden">
        <div className="relative bg-gradient-to-br from-purple-50 to-pink-50 p-6 pt-7">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-gray-500"
            aria-label="Close"
            data-testid="inapp-blocker-close"
          >
            <X size={18} weight="bold" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
              <Warning size={22} weight="fill" className="text-yellow-600" />
            </div>
            <h2 id="inapp-blocker-title" className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Open Dropio in your browser
            </h2>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed" data-testid="inapp-blocker-message">
            Google Sign-In is not supported inside {appName === 'WebView' ? 'embedded' : appName} browsers due to
            Google&apos;s security requirements. Please open Dropio in <strong>Safari</strong> or <strong>Chrome</strong> and try again.
          </p>
        </div>

        <div className="p-5 space-y-3">
          <button
            onClick={openExternal}
            className="w-full dropio-gradient-cta text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-purple-200 hover:shadow-xl transition-all"
            data-testid="inapp-blocker-open-browser"
          >
            <ArrowSquareOut size={18} weight="bold" /> Open in Browser
          </button>

          <button
            onClick={copy}
            className="w-full bg-white border border-gray-200 hover:border-[#6D28D9]/50 text-gray-900 font-semibold py-3 rounded-xl flex items-center justify-center gap-2"
            data-testid="inapp-blocker-copy-link"
          >
            <Copy size={18} weight="bold" /> {copied ? 'Link copied' : 'Copy Link'}
          </button>

          {/* Step-by-step instructions per OS — in case the buttons can't programmatically open the OS browser */}
          <div className="mt-2 p-3 rounded-lg bg-gray-50 border border-gray-100 text-xs text-gray-600 leading-relaxed">
            {os === 'ios' ? (
              <>
                <strong className="text-gray-800">iPhone:</strong> tap the <span className="font-semibold">•••</span> (or share) icon at the top right of {appName} →
                <span className="font-semibold"> &ldquo;Open in Safari&rdquo;</span>. Or paste the copied link into Safari directly.
              </>
            ) : os === 'android' ? (
              <>
                <strong className="text-gray-800">Android:</strong> tap the <span className="font-semibold">•••</span> menu at the top right of {appName} →
                <span className="font-semibold"> &ldquo;Open in Chrome&rdquo;</span>. Or paste the copied link into Chrome directly.
              </>
            ) : (
              <>Long-press the address bar → <span className="font-semibold">&ldquo;Open in Browser&rdquo;</span>, or paste the copied link into your default browser.</>
            )}
          </div>

          <div className="pt-2 flex items-center justify-center gap-1.5 text-xs text-gray-400">
            <GoogleLogo size={14} weight="bold" />
            <span>Required by Google&apos;s OAuth security policy</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InAppBrowserBlockerModal;
